// 「專案的製作」（帳號不是全站製作，但在某個專案的角色是製作，簡稱 PM）要用的純判斷：
//   - 哪些專案是他管的（清單頁左邊選單、「用戶」頁、登入後去哪都看這個）
//   - 「用戶」頁選哪個專案（網址 ?project=）
//   - 清單頁左邊選單有哪幾項
// 全站製作不在這裡：他有自己的管理頁（側欄、/admin/users），畫面不變。

import type { UserRole } from './api';

/** 「用戶」頁的網址 */
export const MY_USERS_PATH = '/users';
/** 「用戶」頁選專案放在網址哪個參數（?project=） */
export const MANAGED_PROJECT_PARAM = 'project';

/** 判斷只需要專案代號和「我在這個專案的角色」 */
export interface ManagedCandidate {
  id: string;
  myRole?: string | null;
}

/**
 * 這個人管的專案（專案的角色是製作）。
 * 全站製作 / 沒登入 → 空（全站製作有自己的管理頁，不走這條路）。
 */
export function managedProjects<P extends ManagedCandidate>(
  accountRole: UserRole | null | undefined,
  projects: ReadonlyArray<P> | null | undefined,
): P[] {
  if (!accountRole || accountRole === 'admin' || !projects) return [];
  return projects.filter(p => p.myRole === 'admin');
}

/** 是不是專案的製作：帳號不是全站製作，而且至少在一個專案的角色是製作 */
export function isProjectManager(
  accountRole: UserRole | null | undefined,
  projects: ReadonlyArray<ManagedCandidate> | null | undefined,
): boolean {
  return managedProjects(accountRole, projects).length > 0;
}

/**
 * 「用戶」頁現在看哪個專案。
 *   - 網址 ?project= 是他管的專案 → 就是它
 *   - 不是他管的（亂填、別人的專案、已經被移出）→ 當作沒選
 *   - 沒選：第一個專案（頁面一打開就看得到人，上面的下拉隨時可以換）
 */
export function pickManagedProject<P extends { id: string }>(
  managed: ReadonlyArray<P>,
  param: string | null | undefined,
): P | null {
  if (param) {
    const hit = managed.find(p => p.id === param);
    if (hit) return hit;
  }
  return managed[0] ?? null;
}

export type ManagerNavKey = 'projects' | 'users';

export interface ManagerNavItem {
  key: ManagerNavKey;
  label: string;
  path: string;
}

/** 清單頁 / 用戶頁左邊的選單：專案（回自己的清單，網址用 landing.ts 的 projectListPath）、用戶 */
export function managerNavItems(projectsPath: string): ManagerNavItem[] {
  return [
    { key: 'projects', label: '專案', path: projectsPath },
    { key: 'users', label: '用戶', path: MY_USERS_PATH },
  ];
}
